import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCommunity } from '@contexts/CommunityContext';
import api from '@helpers/api';

export default function useSubmissions() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { community, loading: communityLoading } = useCommunity();

    const [submissions, setSubmissions] = useState([]);
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [records, setRecords] = useState(0);
    const [search, setSearch] = useState('');
    const [searchParams] = useSearchParams();
    const [formId, setFormId] = useState(() => searchParams.get('form_id'));
    const [forms, setForms] = useState([]);

    const currentCommunity = community.getBySlug(slug);
    const initializedForRef = useRef(null);

    useEffect(() => {
        if (!communityLoading && currentCommunity?.id && initializedForRef.current !== currentCommunity.id) {
            initializedForRef.current = currentCommunity.id;
            loadSubmissions(1);
            loadForms();
        }
    }, [communityLoading, currentCommunity?.id]);

    async function loadForms() {
        try {
            const { data } = await api.get(`communities/${currentCommunity.id}/forms`);
            const list = Array.isArray(data) ? data : data?.data ?? [];
            setForms(list);
        } catch (e) {
            console.error(e);
        }
    }

    async function loadSubmissions(p = 1, q = search, fId = formId) {
        setLoading(true);
        try {
            const queryParams = { page: p || 1, per_page: 20 };
            if (q) queryParams.search = q;
            if (fId) queryParams.form_id = fId;

            const { data } = await api.get(
                `communities/${currentCommunity.id}/forms/submissions`,
                queryParams
            );
            setSubmissions(data?.data ?? []);
            setInsights(data?.insights ?? null);
            setPages(data?.meta?.last_page ?? 1);
            setRecords(data?.meta?.total ?? 0);
            setPage(p);
        } catch (e) {
            console.error(e);
            setSubmissions([]);
        }
        setLoading(false);
    }

    function handleSelectPage(p) {
        loadSubmissions(p, search, formId);
    }

    function handleSearch(value) {
        setSearch(value);
        loadSubmissions(1, value, formId);
    }

    function handleFormFilter(id) {
        setFormId(id);
        loadSubmissions(1, search, id);
    }

    function handleViewSubmission(submission) {
        navigate(`/${slug}/settings/forms/submissions/${submission.id}?form_id=${submission.form_id}`);
    }

    return {
        submissions,
        insights,
        loading,
        page,
        pages,
        records,
        search,
        formId,
        forms,
        handleSearch,
        handleSelectPage,
        handleFormFilter,
        handleViewSubmission,
    };
}
